import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { parseSetlistText } from './SetlistEditor';
import { SETLIST_LABELS } from './structuredDayListLabels';

export const SETLIST_SONGS_PER_PAGE = 12;

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Large headline date for print/share, e.g. "Friday, June 14th 2025". */
export function formatSetlistDateLabel(dateKey) {
  const parts = String(dateKey || '').split('-');
  if (parts.length !== 3) return String(dateKey || '').trim();
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (Number.isNaN(d.getTime())) return String(dateKey || '').trim();
  const day = d.getDate();
  const mod100 = day % 100;
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? 'th' : day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th';
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const month = d.toLocaleDateString(undefined, { month: 'long' });
  return `${weekday}, ${month} ${day}${suffix} ${d.getFullYear()}`;
}

function noteLinesFromSong(song) {
  return (song?.lines || [])
    .map((line) => (line.fields || []).map((f) => String(f || '').trim()).filter(Boolean).join(' — '))
    .filter(Boolean);
}

const BPM_RE = /\bbpm\s*:?\s*(\d{2,3})\b/i;
const DURATION_RE = /(?:len(?:gth)?|dur(?:ation)?|time)\s*:?\s*(\d{1,2}):(\d{2})\b/i;
const BARE_DURATION_RE = /\b(\d{1,2}):(\d{2})\b/;

export function parseSetlistSongTiming(song) {
  const blob = noteLinesFromSong(song).join(' ');
  const bpmMatch = blob.match(BPM_RE);
  const stored = String(song?.duration || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  const storedOk = stored && Number(stored[2]) < 60;
  const durMatch = storedOk ? stored : (blob.match(DURATION_RE) || blob.match(BARE_DURATION_RE));
  const minutes = durMatch ? Number(durMatch[1]) : 0;
  const seconds = durMatch ? Number(durMatch[2]) : 0;
  const bpmField = Number(song?.bpm);
  return {
    bpm: Number.isFinite(bpmField) && bpmField >= 40 ? Math.round(bpmField) : (bpmMatch ? Number(bpmMatch[1]) : null),
    seconds: Number.isFinite(minutes) && Number.isFinite(seconds) ? minutes * 60 + seconds : 0,
  };
}

export function formatSetlistDuration(totalSeconds) {
  const n = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function summarizeSetlistTiming(raw) {
  const { songs } = parseSetlistText(raw);
  let seconds = 0;
  let bpmSum = 0;
  let bpmCount = 0;
  (songs || []).forEach((song) => {
    const t = parseSetlistSongTiming(song);
    seconds += t.seconds;
    if (t.bpm) {
      bpmSum += t.bpm;
      bpmCount += 1;
    }
  });
  return {
    songCount: (songs || []).length,
    seconds,
    lengthLabel: formatSetlistDuration(seconds),
    avgBpm: bpmCount ? Math.round(bpmSum / bpmCount) : null,
  };
}

/**
 * @param {string} raw
 * @param {object} [labels]
 * @returns {{ number: number, title: string, note: string }[]}
 */
export function buildSetlistSongDisplayList(raw, labels = SETLIST_LABELS) {
  const { songs } = parseSetlistText(raw);
  const fallback = labels.itemFallback || 'Song';
  return songs
    .map((song, index) => {
      const title = String(song.title || '').trim();
      const notes = noteLinesFromSong(song);
      const timing = parseSetlistSongTiming(song);
      const timeNote = timing.seconds ? formatSetlistDuration(timing.seconds) : '';
      const bpmNote = timing.bpm ? `BPM ${timing.bpm}` : '';
      const skipTimingNote = (note) => BPM_RE.test(note) || DURATION_RE.test(note) || BARE_DURATION_RE.test(String(note || '').trim());
      const noteParts = [timeNote, bpmNote, ...notes.filter((n) => !skipTimingNote(n))];
      if (title) return { number: index + 1, title, note: noteParts.filter(Boolean).join(' · ') };
      if (notes.length) {
        return { number: index + 1, title: notes[0], note: [bpmNote, ...notes.slice(1)].filter(Boolean).join(' · ') };
      }
      return { number: index + 1, title: `${fallback} ${index + 1}`, note: bpmNote };
    })
    .filter((row) => String(row.title || '').trim());
}

/** @param {{ number: number, title: string }[]} songs */
export function chunkSetlistPages(songs, perPage = SETLIST_SONGS_PER_PAGE) {
  if (!songs.length) return [[]];
  const pages = [];
  for (let i = 0; i < songs.length; i += perPage) {
    pages.push(songs.slice(i, i + perPage));
  }
  return pages;
}

function computeSongFontSize(songCount, pageHeightPx = 1056) {
  const count = Math.max(1, Math.min(SETLIST_SONGS_PER_PAGE, songCount || 1));
  const usable = pageHeightPx - 180;
  return Math.min(76, Math.max(24, (usable / count) * 0.52));
}

function buildSetlistPageHtml({ dateLabel, tourName, venueLine, pageSongs, pageIndex, pageCount }) {
  const count = Math.max(1, pageSongs.length);
  const songsHtml = pageSongs
    .map((song) => {
      const note = String(song.note || '').trim();
      const noteHtml = note ? `<div class="setlist-song-note">${escapeHtml(note)}</div>` : '';
      return `<div class="setlist-song"><div class="setlist-song-main"><span class="setlist-song-num">${song.number}.</span> ${escapeHtml(song.title)}</div>${noteHtml}</div>`;
    })
    .join('');
  const tourHtml = tourName
    ? `<div class="setlist-tour">${escapeHtml(tourName)}${pageCount > 1 ? ` · Page ${pageIndex + 1} of ${pageCount}` : ''}</div>`
    : pageCount > 1
      ? `<div class="setlist-tour">Page ${pageIndex + 1} of ${pageCount}</div>`
      : '';
  const venueHtml = venueLine ? `<div class="setlist-venue">${escapeHtml(venueLine)}</div>` : '';

  return `<section class="setlist-page" style="--song-count: ${count};">
  ${tourHtml}
  <h1 class="setlist-date">${escapeHtml(dateLabel)}</h1>
  ${venueHtml}
  <div class="setlist-songs">${songsHtml}</div>
</section>`;
}

export function buildSetlistPrintDocumentHtml({
  dateLabel,
  setlistRaw,
  tourName = '',
  venueLine = '',
  labels = SETLIST_LABELS,
  autoPrint = false,
}) {
  const songs = buildSetlistSongDisplayList(setlistRaw, labels);
  const pages = chunkSetlistPages(songs);
  const body = pages
    .map((pageSongs, index) =>
      buildSetlistPageHtml({
        dateLabel,
        tourName,
        venueLine,
        pageSongs,
        pageIndex: index,
        pageCount: pages.length,
      })
    )
    .join('\n');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(dateLabel)} — Setlist</title>
  <style>
    @page { size: letter portrait; margin: 0; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
      -webkit-font-smoothing: antialiased;
    }
    .setlist-page {
      width: 100%;
      min-height: 100vh;
      height: 100vh;
      display: flex;
      flex-direction: column;
      padding: 44px 56px 52px;
      box-sizing: border-box;
      page-break-after: always;
      break-after: page;
    }
    .setlist-page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .setlist-tour {
      flex-shrink: 0;
      text-align: center;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #666;
      margin: 0 0 10px;
    }
    .setlist-date {
      flex-shrink: 0;
      margin: 0 0 8px;
      padding: 0;
      text-align: center;
      font-size: clamp(34px, 5.8vh, 58px);
      font-weight: 800;
      letter-spacing: -0.02em;
      line-height: 1.08;
    }
    .setlist-venue {
      flex-shrink: 0;
      text-align: center;
      font-size: 15px;
      font-weight: 600;
      color: #555;
      margin: 0 0 14px;
    }
    .setlist-songs {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      min-height: 0;
      overflow: hidden;
    }
    .setlist-song {
      flex: 1 1 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-weight: 700;
      line-height: 1.12;
      padding: 0 16px;
      font-size: min(76px, max(24px, calc((100vh - 210px) / var(--song-count) * 0.48)));
      max-height: calc((100vh - 210px) / var(--song-count));
    }
    .setlist-song-main {
      min-width: 0;
    }
    .setlist-song-num {
      opacity: 0.45;
      font-weight: 800;
      margin-right: 0.18em;
    }
    .setlist-song-note {
      margin-top: 0.12em;
      font-size: 0.34em;
      font-weight: 500;
      letter-spacing: 0.01em;
      color: #555;
      line-height: 1.25;
    }
    @media print {
      .setlist-page { height: 100vh; }
      .no-print { display: none !important; }
    }
    @media screen {
      body { background: #ececec; }
      .setlist-page {
        max-width: 816px;
        margin: 0 auto 24px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.12);
        background: #fff;
      }
    }
  </style>
</head>
<body>
  ${body || `<section class="setlist-page"><h1 class="setlist-date">${escapeHtml(dateLabel)}</h1><div class="setlist-songs"><div class="setlist-song" style="--song-count:1">No songs yet.</div></div></section>`}
  ${
    autoPrint
      ? `<script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>`
      : ''
  }
</body>
</html>`;
}

export function openSetlistPrintWindow({
  dateLabel,
  setlistRaw,
  tourName = '',
  venueLine = '',
  labels = SETLIST_LABELS,
  autoPrint = true,
}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Print is only available on web.');
  }
  const html = buildSetlistPrintDocumentHtml({
    dateLabel,
    setlistRaw,
    tourName,
    venueLine,
    labels,
    autoPrint,
  });
  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) {
    throw new Error('Pop-up blocked. Allow pop-ups to print the setlist.');
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  return win;
}

export function openSetlistPrintAllWindow({
  days = [],
  tourName = '',
  labels = SETLIST_LABELS,
  autoPrint = true,
}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Print is only available on web.');
  }
  const list = (days || []).filter((d) => String(d?.setlistRaw || '').trim());
  if (!list.length) {
    throw new Error('No setlists on this tour month to print.');
  }
  const docs = list.map((d) => buildSetlistPrintDocumentHtml({
    dateLabel: d.dateLabel || formatSetlistDateLabel(d.dateKey),
    setlistRaw: d.setlistRaw,
    tourName,
    venueLine: d.venueLine || '',
    labels,
    autoPrint: false,
  }));
  const style = String(docs[0] || '').match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const sections = docs
    .map((html) => {
      const body = String(html).match(/<body[^>]*>([\s\S]*)<\/body>/i);
      return body ? body[1].replace(/<script[\s\S]*?<\/script>/gi, '') : '';
    })
    .join('\n');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(tourName || 'Setlists')}</title>
<style>${style ? style[1] : '@page{size:letter portrait;margin:0}body{margin:0;background:#fff}'}</style>
</head><body>${sections}${autoPrint ? '<script>window.addEventListener("load",function(){window.focus();window.print();});</script>' : ''}</body></html>`;
  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) throw new Error('Pop-up blocked. Allow pop-ups to print setlists.');
  win.document.open();
  win.document.write(html);
  win.document.close();
  return win;
}

/**
 * Full-page or inline setlist layout for share views and previews.
 */
export function SetlistPaper({
  dateLabel,
  setlistRaw,
  tourName = '',
  venueLine = '',
  labels = SETLIST_LABELS,
  variant = 'inline',
  theme,
}) {
  const pages = useMemo(() => {
    const songs = buildSetlistSongDisplayList(setlistRaw, labels);
    return chunkSetlistPages(songs);
  }, [setlistRaw, labels]);

  const textColor = theme?.text?.primary || '#111';
  const mutedColor = theme?.text?.secondary || '#666';
  const pageBg = theme?.background?.card || '#fff';
  const isFullPage = variant === 'fullPage';

  if (!pages[0]?.length) {
    return (
      <View style={[styles.emptyWrap, isFullPage && styles.emptyWrapFull]}>
        <Text style={[styles.emptyText, { color: mutedColor }]}>No setlist songs yet.</Text>
      </View>
    );
  }

  return (
    <View style={[styles.stack, isFullPage && styles.stackFull]}>
      {pages.map((pageSongs, pageIndex) => {
        const songFontSize = computeSongFontSize(pageSongs.length);
        return (
          <View
            key={`setlist-page-${pageIndex}`}
            style={[
              styles.page,
              isFullPage ? styles.pageFull : styles.pageInline,
              { backgroundColor: pageBg },
              Platform.OS === 'web'
                ? { boxShadow: isFullPage ? '0 8px 32px rgba(0,0,0,0.12)' : undefined }
                : null,
            ]}
          >
            {tourName ? (
              <Text style={[styles.tourLabel, { color: mutedColor }]} numberOfLines={2}>
                {tourName}
                {pages.length > 1 ? ` · Page ${pageIndex + 1} of ${pages.length}` : ''}
              </Text>
            ) : pages.length > 1 ? (
              <Text style={[styles.tourLabel, { color: mutedColor }]}>
                {`Page ${pageIndex + 1} of ${pages.length}`}
              </Text>
            ) : null}
            <Text style={[styles.dateHeadline, { color: textColor }]}>{dateLabel}</Text>
            {venueLine ? (
              <Text style={[styles.venueLine, { color: mutedColor }]}>{venueLine}</Text>
            ) : null}
            <View style={styles.songList}>
              {pageSongs.map((song) => (
                <View key={`song-${song.number}`} style={styles.songRow}>
                  <Text
                    style={[styles.songText, { color: textColor, fontSize: songFontSize, lineHeight: songFontSize * 1.12 }]}
                  >
                    <Text style={[styles.songNum, { color: mutedColor }]}>{song.number}. </Text>
                    {song.title}
                  </Text>
                  {song.note ? (
                    <Text style={[styles.songNote, { color: mutedColor }]}>{song.note}</Text>
                  ) : null}
                </View>
              ))}
            </View>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  stack: { gap: 16, width: '100%' },
  stackFull: { gap: 24, alignItems: 'center' },
  page: {
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    flexDirection: 'column',
  },
  pageInline: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    minHeight: 360,
  },
  pageFull: {
    paddingVertical: 44,
    paddingHorizontal: 48,
    minHeight: Platform.OS === 'web' ? '100vh' : 640,
    maxWidth: 816,
    alignSelf: 'center',
  },
  tourLabel: {
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  dateHeadline: {
    textAlign: 'center',
    fontSize: Platform.OS === 'web' ? 'clamp(28px, 5vw, 48px)' : 34,
    fontWeight: '800',
    letterSpacing: -0.5,
    lineHeight: Platform.OS === 'web' ? 1.08 : 38,
    marginBottom: 8,
  },
  venueLine: {
    textAlign: 'center',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 14,
  },
  songList: {
    flex: 1,
    justifyContent: 'space-evenly',
    minHeight: 240,
    gap: 4,
  },
  songRow: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  songText: {
    textAlign: 'center',
    fontWeight: '700',
  },
  songNote: {
    textAlign: 'center',
    fontSize: 13,
    fontWeight: '500',
    marginTop: 4,
  },
  songNum: {
    fontWeight: '800',
    opacity: 0.45,
  },
  emptyWrap: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  emptyWrapFull: {
    minHeight: 200,
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
